import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { Select } from 'primeng/select';
import { InputText } from 'primeng/inputtext';
import { Tooltip } from 'primeng/tooltip';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { catchError, of } from 'rxjs';
import { Designation } from '../../models/designation.model';
import { DesignationService } from '../../services/designation.service';
import { AuthService } from '../../services/auth.service';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-designation-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    Button,
    Dialog,
    Select,
    InputText,
    Tooltip,
    ConfirmDialog
  ],
  templateUrl: './designation-management.component.html',
  styleUrl: './designation-management.component.scss',
  providers: [ConfirmationService]
})
export class DesignationManagementComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  designations: Designation[] = [];
  filteredDesignations: Designation[] = [];
  selectedDesignation: Designation = this.getEmptyDesignation();
  displayDialog = false;
  dialogMode: 'add' | 'edit' = 'add';
  loading = false;
  searchText: string = '';
  selectedStatusFilter: string | null = 'active';
  fieldTouched: Record<string, boolean> = {};

  rows: number = 50;
  first: number = 0;
  totalRecords: number = 0;
  rowsPerPageOptions: number[] = [5, 10, 20, 50];

  statusFilterOptions: { label: string; value: string | null }[] = [
    { label: 'All', value: null },
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' }
  ];

  get totalDesignationsCount(): number { return this.designations.length; }
  get activeDesignationsCount(): number { return this.designations.filter(d => d.isActive).length; }
  get inactiveDesignationsCount(): number { return this.designations.filter(d => !d.isActive).length; }

  constructor(
    private designationService: DesignationService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private authService: AuthService,
    public pageHeaderService: PageHeaderSearchActionService
  ) {}

  ngOnInit() {
    this.pageHeaderService.setCanShowFilterToggle(true);
    this.pageHeaderService.setPlaceholder('Search by name or description...');
    this.pageHeaderService.setPrimaryButtonLabel('Add Designation');
    this.pageHeaderService.setCanShowExport(true);
    this.pageHeaderService.setExportDisabled(false);
    this.searchText = this.pageHeaderService.currentSearchText;
    this.loadDesignations();
    this.pageHeaderService.searchText.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(v => {
      this.searchText = v ?? '';
      this.applySearch();
    });
    this.pageHeaderService.primaryActionClick.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.showAddDialog());
    this.pageHeaderService.exportClick.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.exportToExcel());
    this.authService.user$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(user => {
      this.pageHeaderService.setCanShowPrimaryAction(!!(user?.isAdmin || user?.isPc));
    });
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearPageConfig();
  }

  loadDesignations() {
    this.loading = true;
    this.designationService.getAllDesignations().pipe(
      catchError(error => {
        console.error('Error loading designations:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load designations. Please refresh the page or try again later.'
        });
        this.loading = false;
        // Return empty array to prevent complete failure
        return of([] as Designation[]);
      })
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        // Ensure isActive is set for all designations (default to true if missing)
        this.designations = data.map(d => ({
          ...d,
          isActive: d.isActive !== undefined ? d.isActive : true
        }));
        // Reapply filters to preserve filtered state
        this.applySearch();
        this.loading = false;
      }
    });
  }

  onSearch() {
    this.applySearch();
  }

  applySearch() {
    let filtered = [...this.designations];

    // Apply search text filter
    if (this.searchText.trim() !== '') {
      const searchLower = this.searchText.toLowerCase();
      filtered = filtered.filter(d =>
        d.name?.toLowerCase().includes(searchLower) ||
        d.description?.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (this.selectedStatusFilter !== null) {
      if (this.selectedStatusFilter === 'active') {
        filtered = filtered.filter(d => d.isActive === true);
      } else if (this.selectedStatusFilter === 'inactive') {
        filtered = filtered.filter(d => d.isActive === false);
      }
    }

    this.filteredDesignations = filtered;
    this.totalRecords = this.filteredDesignations.length;
    this.first = 0;
  }

  clearSearch() {
    this.searchText = '';
    this.applySearch();
  }

  onStatusFilterChange() {
    this.applySearch();
  }

  clearFilters() {
    this.searchText = '';
    this.pageHeaderService.setSearchText('');
    this.selectedStatusFilter = 'active';
    this.applySearch();
  }

  hasActiveFilters(): boolean {
    return !!(this.searchText.trim() ||
      (this.selectedStatusFilter !== null && this.selectedStatusFilter !== 'active'));
  }

  toggleActiveStatus(designation: Designation) {
    const activating = !designation.isActive;
    this.confirmationService.confirm({
      message: `Are you sure you want to ${activating ? 'activate' : 'deactivate'} "${designation.name}"?`,
      header: activating ? 'Confirm Activate' : 'Confirm Deactivate',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: activating ? 'p-button-success' : 'p-button-danger',
      accept: () => {
        const updatedDesignation = { ...designation, isActive: !designation.isActive };
        this.designationService.updateDesignation(designation.id, updatedDesignation).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: `Designation ${updatedDesignation.isActive ? 'activated' : 'deactivated'} successfully`
            });
            this.loadDesignations();
          },
          error: (error) => {
            console.error('Error updating designation status:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to update designation status'
            });
          }
        });
      }
    });
  }

  showAddDialog() {
    this.selectedDesignation = this.getEmptyDesignation();
    this.fieldTouched = {};
    this.dialogMode = 'add';
    this.displayDialog = true;
  }

  showEditDialog(designation: Designation) {
    this.selectedDesignation = { ...designation };
    this.fieldTouched = {};
    this.dialogMode = 'edit';
    this.displayDialog = true;
  }

  hideDialog() {
    this.displayDialog = false;
    this.fieldTouched = {};
    this.selectedDesignation = this.getEmptyDesignation();
  }

  resetDesignationForm() {
    this.selectedDesignation = this.getEmptyDesignation();
    this.fieldTouched = {};
  }

  exportToExcel() {
    if (this.filteredDesignations.length === 0) return;
    const headers = ['Name', 'Description', 'Status'];
    const rows = this.filteredDesignations.map(d => [
      d.name ?? '',
      d.description ?? '',
      d.isActive ? 'Active' : 'Inactive'
    ]);
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Designations');
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([wbout], { type: 'application/octet-stream' }), `designations_${this.formatDateForFile()}.xlsx`);
  }

  formatDateForFile(): string {
    const d = new Date();
    return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}_${String(d.getHours()).padStart(2, '0')}${String(d.getMinutes()).padStart(2, '0')}`;
  }

  saveDesignation() {
    this.fieldTouched['name'] = true;
    if (!this.selectedDesignation.name.trim()) {
      return;
    }

    if (this.dialogMode === 'add') {
      this.designationService.addDesignation(this.selectedDesignation).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Designation added successfully'
          });
          this.loadDesignations();
          this.hideDialog();
        },
        error: (error) => {
          console.error('Error adding designation:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to add designation'
          });
        }
      });
    } else {
      this.designationService.updateDesignation(this.selectedDesignation.id, this.selectedDesignation).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Designation updated successfully'
          });
          this.loadDesignations();
          this.hideDialog();
        },
        error: (error) => {
          console.error('Error updating designation:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update designation'
          });
        }
      });
    }
  }

  private getEmptyDesignation(): Designation {
    return {
      id: '',
      name: '',
      description: '',
      isActive: true
    };
  }
}

